import { KartoyIcon, KartoyQrCode } from '@mobile/components/kartoy/kartoy-icon'
import { PillButton } from '@mobile/components/kartoy/pill-button'
import {
  isCardIssuePaymentActive,
  issueCard,
  restoreCardIssue,
  selectedCardIssue,
  selectedCurrentUser,
} from '@shared-core/features/backend'
import { selectedUserPhone } from '@shared-core/features/settings'
import { useCardTemplates } from '@shared-core/hooks/use-card-templates'
import { useI18N } from '@shared-core/hooks/use-i18n'
import { type RootRouteProps, ScreensList, useNavigation, useRoute } from '@shared-core/navigation'
import { useAppDispatch, useAppSelector } from '@shared-core/store'
import { formatWithThousandSeparators } from '@shared-core/utils/balances/balances'
import { formatPhoneDisplay } from '@shared-core/utils/phone'
import { AppIcon } from '@shared-ui/components/ui/app-icon'
import { PaymentQr } from '@shared-ui/components/ui/payment-qr'
import { ScreenWrapper } from '@shared-ui/components/ui/screen-wrappers/screen-wrapper'
import { GText } from '@shared-ui/components/ui/text'
import { useClipboard } from '@shared-ui/platform/hooks/use-clipboard'
import { handleOpenLink } from '@shared-ui/platform/link'
import { useEffect, useRef, useState } from 'react'
import { Pressable, View } from 'react-native'
import Animated, { FadeInDown } from 'react-native-reanimated'

import locales from './index.i18n.json'
import { useCreateStyles } from './styles'

function formatTimer(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60

  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
}

type ScreenPaymentLinkProps = {
  route?: RootRouteProps<typeof ScreensList.PAYMENT_LINK>
}

export function ScreenPaymentLink({ route }: ScreenPaymentLinkProps) {
  const styles = useCreateStyles()
  const { t } = useI18N(locales)
  const navigation = useNavigation()

  const currentRoute = useRoute<RootRouteProps<typeof ScreensList.PAYMENT_LINK>>()
  const params = route?.params ?? currentRoute.params
  const dispatch = useAppDispatch()
  const templates = useCardTemplates()
  const template = templates.find((item) => item.id === params?.templateId)
  const cardIssue = useAppSelector(selectedCardIssue)
  const currentUser = useAppSelector(selectedCurrentUser)
  const userPhone = useAppSelector(selectedUserPhone)
  const { handleCopy } = useClipboard()
  const existingInvoice = cardIssue.invoice
  const issueRequestedRef = useRef(false)
  const invoice =
    params?.topup ??
    (existingInvoice &&
    ((params?.txId && existingInvoice.txId === params.txId) ||
      (params?.templateId && existingInvoice.templateId === params.templateId))
      ? existingInvoice
      : null)
  const isIssue = !params?.topup && Boolean(params?.templateId || params?.txId)
  const isPayment = isIssue || Boolean(params?.topup)
  const paymentAmount = invoice?.amount ?? (isIssue ? template?.issuePrice : undefined)
  let amount = params?.amount ?? '0'

  if (isPayment) amount = paymentAmount ? formatWithThousandSeparators(paymentAmount.replace(/\.00$/, '')) : '—'
  const [now, setNow] = useState(Date.now())
  const expiresAt = invoice ? invoice.expiresAt : params?.expiresAt
  const secondsLeft = typeof expiresAt === 'number' ? Math.max(0, Math.ceil((expiresAt * 1000 - now) / 1000)) : 0
  const isExpired = invoice?.state === 'expired' || (typeof expiresAt === 'number' && secondsLeft === 0)
  const isFailed = invoice?.state === 'failed'
  const phone = formatPhoneDisplay(currentUser?.phone?.number || userPhone)
  const link = invoice?.paymentLink ?? invoice?.qrUrl ?? params?.qrUrl
  const canPay = Boolean(link && !isExpired && !isFailed && invoice?.state !== 'completed')
  // Создаём один счёт; возврат к существующему QR не запускает повторный выпуск.
  useEffect(() => {
    if (params?.txId) {
      if (!issueRequestedRef.current) {
        issueRequestedRef.current = true
        void dispatch(restoreCardIssue({ txId: params.txId, templateId: params.templateId }))
      }

      return
    }

    if (!params?.templateId || cardIssue.isLoading || issueRequestedRef.current) return

    if (
      params.txId ||
      (existingInvoice?.templateId === params.templateId && isCardIssuePaymentActive(existingInvoice))
    ) {
      issueRequestedRef.current = true
      return
    }
    issueRequestedRef.current = true
    void dispatch(issueCard({ templateId: params.templateId }))
  }, [dispatch, params?.templateId, params?.txId, existingInvoice, cardIssue.isLoading])

  // Таймер считает реальный срок действия ссылки, в том числе после возврата из банка.
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now())
    }, 1000)

    return () => clearInterval(timer)
  }, [])

  let qrCaption: 'active-for' | 'preparing' | 'expired' | 'failed' = 'active-for'

  if (isIssue && !invoice) qrCaption = 'preparing'

  if (isExpired) qrCaption = 'expired'

  if (isFailed) qrCaption = 'failed'

  return (
    <ScreenWrapper style={styles.container} showsVerticalScrollIndicator={false}>
      <Animated.View entering={FadeInDown.duration(380)} style={styles.close}>
        <Pressable accessibilityRole="button" hitSlop={8} onPress={() => navigation.popTo(ScreensList.HOME_START)}>
          <AppIcon icon="close" size={24} />
        </Pressable>
      </Animated.View>

      <Animated.View entering={FadeInDown.delay(40).duration(380)} style={styles.titleBlock}>
        <GText style={styles.label}>{t('title')}</GText>
        <GText fw={600} style={styles.amount}>
          {`₽${amount}`}
        </GText>
      </Animated.View>

      <Animated.View entering={FadeInDown.delay(100).duration(400)} style={styles.qrCard}>
        <PaymentQr
          isLoading={isIssue && !invoice}
          caption={t(qrCaption)}
          time={isExpired || isFailed || typeof expiresAt !== 'number' ? undefined : formatTimer(secondsLeft)}
        >
          <KartoyQrCode size={180} value={invoice?.qrUrl ?? invoice?.paymentLink ?? params?.qrUrl} />
        </PaymentQr>
      </Animated.View>

      {cardIssue.error && isIssue && (
        <View style={styles.errorBlock}>
          <GText isDanger>{t('error')}</GText>
          <PillButton
            title={t('retry')}
            onPress={() => {
              if (params?.templateId) void dispatch(issueCard({ templateId: params.templateId }))
            }}
          />
        </View>
      )}

      <Animated.View entering={FadeInDown.delay(160).duration(400)} style={styles.divider} />

      <Animated.View entering={FadeInDown.delay(220).duration(400)} style={styles.phoneBlock}>
        <View style={styles.phoneInfo}>
          <View style={styles.phoneCaptionRow}>
            <KartoyIcon icon="phone" size={24} />
            <GText style={styles.label}>{t('pay-from')}</GText>
          </View>
          <GText fw={600} style={styles.phone}>
            {phone}
          </GText>
        </View>
        <GText style={styles.note}>{t('note')}</GText>
        {isIssue && <GText style={styles.note}>{t('close-hint')}</GText>}
      </Animated.View>

      {(!isIssue || invoice) && (
        <Animated.View entering={FadeInDown.delay(280).duration(400)} style={styles.buttonsBlock}>
          <PillButton title={t('go-pay')} icon="goPay" disabled={!canPay} onPress={handleOpenLink(link)} />
          <PillButton title={t('copy')} variant="secondary" disabled={!canPay} onPress={handleCopy(link ?? '')} />
        </Animated.View>
      )}
    </ScreenWrapper>
  )
}
